import type { MagiMethod } from "../config";
import type { EffectiveLimits } from "../types";
import { methodRequiresOptions } from "../methods";

export type ValidationIssue = { field: string; message: string };

export function validateSubmission(params: {
  prompt: string;
  method: MagiMethod;
  options: string[];
  selectedLlms: string[];
  rapporteurPrompt?: string;
  systemPrompt?: string;
  language?: string;
  limits: EffectiveLimits;
  allowedMethods: MagiMethod[];
}): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const {
    prompt,
    method,
    options,
    selectedLlms,
    rapporteurPrompt = "",
    systemPrompt = "",
    language = "",
    limits,
    allowedMethods,
  } = params;

  if (!prompt.trim()) {
    issues.push({ field: "prompt", message: "Please enter a question." });
  } else if (prompt.length > limits.maxPromptLength) {
    issues.push({
      field: "prompt",
      message: `Question exceeds maximum length (${prompt.length}/${limits.maxPromptLength}).`,
    });
  }

  if (rapporteurPrompt.length > limits.maxRapporteurPromptLength) {
    issues.push({
      field: "rapporteurPrompt",
      message: `Rapporteur prompt exceeds maximum length (${rapporteurPrompt.length}/${limits.maxRapporteurPromptLength}).`,
    });
  }
  if (systemPrompt.length > limits.maxSystemPromptLength) {
    issues.push({
      field: "systemPrompt",
      message: `System prompt exceeds maximum length (${systemPrompt.length}/${limits.maxSystemPromptLength}).`,
    });
  }
  if (language.length > limits.maxLanguageLength) {
    issues.push({
      field: "language",
      message: `Response language exceeds maximum length (${language.length}/${limits.maxLanguageLength}).`,
    });
  }

  if (selectedLlms.length < 2) {
    issues.push({
      field: "llms",
      message:
        "Select at least two models — deliberation needs more than one voice.",
    });
  }

  if (!allowedMethods.includes(method)) {
    issues.push({
      field: "method",
      message: `Method ${method} is not permitted for this service token.`,
    });
  }

  if (methodRequiresOptions(method)) {
    const cleaned = options.map((o) => o.trim()).filter(Boolean);
    if (cleaned.length < 2) {
      issues.push({
        field: "options",
        message: `${method} needs at least two options.`,
      });
    }
    if (cleaned.length > limits.maxOptions) {
      issues.push({
        field: "options",
        message: `Too many options (${cleaned.length}/${limits.maxOptions}).`,
      });
    }
    for (const [i, opt] of cleaned.entries()) {
      if (opt.length > limits.maxOptionLength) {
        issues.push({
          field: "options",
          message: `Option ${i + 1} exceeds the ${limits.maxOptionLength}-character limit.`,
        });
        break;
      }
    }
    const unique = new Set(cleaned.map((o) => o.toLowerCase()));
    if (unique.size !== cleaned.length) {
      issues.push({ field: "options", message: "Options must be unique." });
    }
  }

  return issues;
}
